/**
 * 数据同步服务
 * 负责管理跨平台数据同步、离线缓存和自动同步
 */
import api from './api';

const isBrowser = (): boolean => typeof window !== 'undefined' && typeof navigator !== 'undefined';

const getInitialOnlineStatus = (): boolean => {
  if (!isBrowser()) return true;
  return navigator.onLine;
};

// 同步状态枚举
export enum SyncStatus {
  IDLE = 'idle',
  SYNCING = 'syncing',
  SUCCESS = 'success',
  ERROR = 'error',
  OFFLINE = 'offline'
}

// 缓存数据项接口
export interface CacheItem {
  id: string;
  type: 'worktime' | 'equipment' | 'material' | 'project' | 'priceconfig';
  action: 'create' | 'update' | 'delete';
  data: any;
  timestamp: number;
  synced: boolean;
}

// 同步配置
export interface SyncConfig {
  autoSyncInterval: number; // 自动同步间隔（毫秒）
  maxRetries: number; // 最大重试次数
  retryDelay: number; // 重试延迟（毫秒）
}

class SyncService {
  private syncStatus: SyncStatus = SyncStatus.IDLE;
  private isOnline: boolean = getInitialOnlineStatus();
  private syncTimer: NodeJS.Timeout | null = null;
  private listeners: Array<(status: SyncStatus) => void> = [];
  private config: SyncConfig = {
    autoSyncInterval: 30000, // 30秒
    maxRetries: 3,
    retryDelay: 5000 // 5秒
  };

  constructor() {
    this.initNetworkListener();
    this.startAutoSync();
  }

  /**
   * 初始化网络状态监听
   */
  private initNetworkListener(): void {
    if (!isBrowser()) {
      return;
    }

    window.addEventListener('online', () => {
      this.isOnline = true;
      this.setSyncStatus(SyncStatus.IDLE);
      this.syncPendingData();
    });

    window.addEventListener('offline', () => {
      this.isOnline = false;
      this.setSyncStatus(SyncStatus.OFFLINE);
    });
  }

  /**
   * 启动自动同步
   */
  private startAutoSync(): void {
    if (this.syncTimer) {
      clearInterval(this.syncTimer);
    }

    this.syncTimer = setInterval(() => {
      if (this.isOnline && this.syncStatus !== SyncStatus.SYNCING) {
        this.syncPendingData();
      }
    }, this.config.autoSyncInterval);
  }

  /**
   * 设置同步状态
   */
  private setSyncStatus(status: SyncStatus): void {
    this.syncStatus = status;
    this.listeners.forEach(listener => listener(status));
  }

  /**
   * 添加状态监听器
   */
  public addStatusListener(listener: (status: SyncStatus) => void): void {
    this.listeners.push(listener);
  }

  /**
   * 移除状态监听器
   */
  public removeStatusListener(listener: (status: SyncStatus) => void): void {
    const index = this.listeners.indexOf(listener);
    if (index > -1) {
      this.listeners.splice(index, 1);
    }
  }

  /**
   * 获取当前同步状态
   */
  public getSyncStatus(): SyncStatus {
    return this.syncStatus;
  }

  /**
   * 获取网络状态
   */
  public isNetworkOnline(): boolean {
    return this.isOnline;
  }

  /**
   * 缓存数据到本地存储
   */
  public cacheData(item: Omit<CacheItem, 'id' | 'timestamp' | 'synced'>): void {
    const cacheItem: CacheItem = {
      ...item,
      id: this.generateId(),
      timestamp: Date.now(),
      synced: false
    };

    const cachedData = this.getCachedData();
    cachedData.push(cacheItem);
    localStorage.setItem('sync_cache', JSON.stringify(cachedData));

    // 如果在线，立即尝试同步
    if (this.isOnline) {
      this.syncPendingData();
    }
  }

  /**
   * 获取缓存的数据
   */
  private getCachedData(): CacheItem[] {
    const cached = localStorage.getItem('sync_cache');
    return cached ? JSON.parse(cached) : [];
  }

  /**
   * 同步待处理的数据
   */
  public async syncPendingData(): Promise<void> {
    if (!this.isOnline || this.syncStatus === SyncStatus.SYNCING) {
      return;
    }

    const cachedData = this.getCachedData();
    const pendingData = cachedData.filter(item => !item.synced);

    if (pendingData.length === 0) {
      return;
    }

    this.setSyncStatus(SyncStatus.SYNCING);

    try {
      for (const item of pendingData) {
        await this.syncSingleItem(item);
        this.markItemAsSynced(item.id);
      }

      this.setSyncStatus(SyncStatus.SUCCESS);
      this.cleanupSyncedData();
    } catch (error) {
      console.error('数据同步失败:', error);
      this.setSyncStatus(SyncStatus.ERROR);
      throw error;
    }
  }

  /**
   * 同步单个数据项
   */
  private async syncSingleItem(item: CacheItem): Promise<void> {
    const endpoint = this.getEndpoint(item.type);
    
    try {
      switch (item.action) {
        case 'create':
          await api.post(endpoint, item.data);
          break;
        case 'update':
          await api.put(`${endpoint}/${item.data._id}`, item.data);
          break;
        case 'delete':
          await api.delete(`${endpoint}/${item.data._id}`);
          break;
      }
    } catch (error) {
      console.error(`同步${item.type}数据失败:`, error);
      throw error;
    }
  }

  /**
   * 获取API端点
   */
  private getEndpoint(type: string): string {
    const endpoints = {
      worktime: '/worktimes',
      equipment: '/equipment-usage',
      material: '/material-usage',
      project: '/projects',
      priceconfig: '/price-config'
    };
    return endpoints[type as keyof typeof endpoints] || '';
  }

  /**
   * 标记数据项为已同步
   */
  private markItemAsSynced(itemId: string): void {
    const cachedData = this.getCachedData();
    const item = cachedData.find(item => item.id === itemId);
    if (item) {
      item.synced = true;
      localStorage.setItem('sync_cache', JSON.stringify(cachedData));
    }
  }

  /**
   * 清理已同步的数据
   */
  private cleanupSyncedData(): void {
    const cachedData = this.getCachedData();
    const pendingData = cachedData.filter(item => !item.synced);
    localStorage.setItem('sync_cache', JSON.stringify(pendingData));
  }

  /**
   * 生成唯一ID
   */
  private generateId(): string {
    return `${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  /**
   * 手动触发同步
   */
  public async manualSync(): Promise<void> {
    return this.syncPendingData();
  }

  /**
   * 获取待同步数据数量
   */
  public getPendingCount(): number {
    const cachedData = this.getCachedData();
    return cachedData.filter(item => !item.synced).length;
  }

  /**
   * 清空所有缓存数据
   */
  public clearCache(): void {
    localStorage.removeItem('sync_cache');
  }

  /**
   * 销毁服务
   */
  public destroy(): void {
    if (this.syncTimer) {
      clearInterval(this.syncTimer);
      this.syncTimer = null;
    }
    this.listeners = [];
  }
}

// 创建单例实例
export const syncService = new SyncService();
export default syncService;
